import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUpRight,
  Clock,
  Users,
  MapPin,
  Check,
  MessageCircle,
  Send,
  Star,
  Phone,
  Mail,
  Menu,
  X,
} from "lucide-react";
import { BRAND, TOURS, GROUP_TOURS, FAQ } from "@/data/tours";
import BookingDialog from "@/components/BookingDialog";
import WhatsAppFloat from "@/components/WhatsAppFloat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const setCanonical = (url) => {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
};

const setJsonLd = (obj) => {
  const id = "jsonld-tour";
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(obj);
};

/* Compact site header (variant used on inner pages) */
function InnerHeader({ onBook }) {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="site-header" className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF7F2]/85 border-b border-border">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="brand-logo" className="font-display text-xl md:text-[22px] tracking-tight text-foreground">
          <span className="font-semibold">ДЕЛИ</span>
          <span className="opacity-60 mx-2">·</span>
          <span className="italic">с Али</span>
        </Link>
        <nav className="hidden md:flex items-center gap-9">
          <Link to="/#tours" className="text-[13px] font-medium text-foreground/80 hover:text-foreground link-editorial">Экскурсии</Link>
          <Link to="/#about" className="text-[13px] font-medium text-foreground/80 hover:text-foreground link-editorial">О гиде</Link>
          <Link to="/#reviews" className="text-[13px] font-medium text-foreground/80 hover:text-foreground link-editorial">Отзывы</Link>
          <Link to="/#faq" className="text-[13px] font-medium text-foreground/80 hover:text-foreground link-editorial">Вопросы</Link>
          <Link to="/#contact" className="text-[13px] font-medium text-foreground/80 hover:text-foreground link-editorial">Контакты</Link>
        </nav>
        <div className="flex items-center gap-3">
          <a href={BRAND.phoneHref} data-testid="header-phone" className="hidden lg:inline-flex items-center gap-2 text-[13px] font-medium text-foreground">
            <Phone className="h-4 w-4" />{BRAND.phone}
          </a>
          <button data-testid="header-book-btn" onClick={onBook} className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#F07E26] text-white px-5 py-2.5 text-[13px] font-semibold hover:bg-[#d86a15] transition">
            Забронировать <ArrowUpRight className="h-4 w-4" />
          </button>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(v => !v)} className="md:hidden rounded-full p-2 border border-border text-foreground" aria-label="Меню">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-[#FAF7F2] border-t border-border">
          <div className="px-6 py-5 flex flex-col gap-4">
            <Link to="/#tours" onClick={() => setOpen(false)} className="text-[15px] font-medium">Экскурсии</Link>
            <Link to="/#about" onClick={() => setOpen(false)} className="text-[15px] font-medium">О гиде</Link>
            <Link to="/#reviews" onClick={() => setOpen(false)} className="text-[15px] font-medium">Отзывы</Link>
            <Link to="/#faq" onClick={() => setOpen(false)} className="text-[15px] font-medium">Вопросы</Link>
            <Link to="/#contact" onClick={() => setOpen(false)} className="text-[15px] font-medium">Контакты</Link>
            <button data-testid="mobile-book-btn" onClick={() => { setOpen(false); onBook(); }} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#F07E26] text-white px-5 py-3 text-[14px] font-semibold">
              Забронировать <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

export default function TourDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tour = useMemo(
    () => TOURS.find((t) => t.id === slug) || GROUP_TOURS.find((t) => t.id === slug),
    [slug]
  );
  const isGroup = tour && GROUP_TOURS.some((t) => t.id === tour.id);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    if (!tour) return;
    const canonical = `${SITE_URL}/tours/${tour.id}`;
    const title = `${tour.title} — экскурсия с гидом Али в Индии | Дели с Али`;
    const description = `${tour.short} Продолжительность: ${tour.duration}. Группа ${tour.group}. Цена: ${tour.price}. Русскоговорящий гид Али в ${tour.city}.`;
    document.title = title;
    setMeta("description", description);
    setMeta("keywords", `${tour.title.toLowerCase()}, экскурсия ${tour.city}, гид ${tour.city} на русском, тур ${tour.city}, ali private india`);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", tour.image, "property");
    setMeta("og:url", canonical, "property");
    setMeta("og:type", "product", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", tour.image);
    setCanonical(canonical);

    // Extract numeric price and currency from tour.price string (e.g., "от $850 / чел.", "$190 до 2 чел....", "от ₽ 24 900 / чел.")
    const priceStr = String(tour.price || "");
    const usdMatch = priceStr.match(/\$\s*([\d\s.,]+)/);
    const rubMatch = priceStr.match(/₽\s*([\d\s.,]+)/);
    let priceNumber, priceCurrency;
    if (usdMatch) {
      priceNumber = parseFloat(usdMatch[1].replace(/[\s,]/g, ""));
      priceCurrency = "USD";
    } else if (rubMatch) {
      priceNumber = parseFloat(rubMatch[1].replace(/[\s,]/g, ""));
      priceCurrency = "RUB";
    }

    const itineraryItems = (tour.program || tour.highlights.map((h) => ({ title: h, desc: "" }))).map((it) => {
      const item = typeof it === "string" ? { title: it, desc: "" } : it;
      return {
        "@type": "TouristAttraction",
        name: item.title,
        ...(item.desc && { description: item.desc }),
      };
    });

    // Multi-type: Product for Google rich results (rating/offers) + TouristTrip for tour semantics.
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": ["Product", "TouristTrip"],
      "@id": canonical,
      name: tour.title,
      description: tour.description || tour.short,
      image: [tour.image],
      url: canonical,
      brand: {
        "@type": "Brand",
        name: "Дели с Али",
      },
      category: "Tour / Excursion",
      touristType: "Русскоговорящие туристы",
      itinerary: {
        "@type": "ItemList",
        numberOfItems: itineraryItems.length,
        itemListElement: itineraryItems.map((att, i) => ({
          "@type": "ListItem",
          position: i + 1,
          item: att,
        })),
      },
      ...(priceNumber && priceCurrency && {
        offers: {
          "@type": "Offer",
          price: priceNumber,
          priceCurrency,
          availability: "https://schema.org/InStock",
          url: canonical,
          validFrom: new Date().toISOString().slice(0, 10),
          priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
          seller: {
            "@type": "TravelAgency",
            name: "Дели с Али",
          },
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry: "IN",
            returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
            merchantReturnDays: 1,
            returnMethod: "https://schema.org/ReturnByMail",
            returnFees: "https://schema.org/FreeReturn",
          },
          shippingDetails: {
            "@type": "OfferShippingDetails",
            shippingRate: {
              "@type": "MonetaryAmount",
              value: 0,
              currency: priceCurrency,
            },
            shippingDestination: {
              "@type": "DefinedRegion",
              addressCountry: "IN",
            },
            deliveryTime: {
              "@type": "ShippingDeliveryTime",
              handlingTime: {
                "@type": "QuantitativeValue",
                minValue: 0,
                maxValue: 0,
                unitCode: "DAY",
              },
              transitTime: {
                "@type": "QuantitativeValue",
                minValue: 0,
                maxValue: 0,
                unitCode: "DAY",
              },
            },
          },
        },
      }),
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.98",
        bestRating: "5",
        worstRating: "1",
        reviewCount: "148",
      },
      provider: {
        "@type": "TravelAgency",
        name: "Дели с Али",
        telephone: BRAND.phone,
        email: BRAND.email,
        url: SITE_URL,
        image: `${SITE_URL}/favicon-512.png`,
        sameAs: [BRAND.instagramHref, BRAND.telegramHref, BRAND.vkHref].filter(Boolean),
      },
    };
    setJsonLd(jsonLd);
    window.scrollTo(0, 0);
  }, [tour]);

  if (!tour) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="overline text-[#D0533C]">404</p>
        <h1 className="font-display text-4xl mt-4">Экскурсия не найдена</h1>
        <button
          onClick={() => navigate("/")}
          data-testid="back-home-btn"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#F07E26] text-white px-6 py-3 text-sm font-semibold"
        >
          <ArrowLeft className="h-4 w-4" /> На главную
        </button>
      </div>
    );
  }

  const related = TOURS.filter((t) => t.id !== tour.id).slice(0, 3);

  const handleSubmit = async (form) => {
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      date: form.date,
      people: Number(form.people),
      tour_id: form.tour_id,
      tour_title: form.tour_title,
      comment: form.comment?.trim() || "",
    };
    const { data } = await axios.post(`${API}/bookings`, payload, { timeout: 20000 });
    return data;
  };

  return (
    <div data-testid="tour-detail-page" data-tour-id={tour.id}>
      <InnerHeader onBook={() => setBookingOpen(true)} />

      {/* Hero */}
      <section className="relative h-[68vh] min-h-[520px] w-full overflow-hidden">
        <img src={tour.image} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/75" />
        <div className="relative z-10 h-full mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 flex flex-col justify-end pb-14">
          <Link to="/#tours" data-testid="breadcrumb-back" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-[13px]">
            <ArrowLeft className="h-4 w-4" /> Все экскурсии
          </Link>
          <p className="overline text-[#F5C58C] mt-6">
            <MapPin className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
            {tour.city}
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="tour-h1"
            className="font-display !font-semibold text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-4 max-w-4xl"
          >
            {tour.title}
          </motion.h1>
          <div className="mt-8 flex flex-wrap items-center gap-6 text-white/90 text-sm">
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {tour.duration}</span>
            <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> {tour.group}</span>
            <span className="inline-flex items-center gap-2"><Star className="h-4 w-4 fill-[#F07E26] text-[#F07E26]" /> 4.98 / 5</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-7 space-y-12">
            <div>
              <p className="overline text-[#D0533C]">Об экскурсии</p>
              <h2 className="font-display text-3xl md:text-4xl mt-4 leading-[1.1]">
                <span className="italic">Что вас ждёт</span> на маршруте
              </h2>
              <p className="mt-6 text-foreground/80 leading-relaxed text-[15px]">
                {tour.description ? (
                  tour.description
                ) : (
                  <>
                    {tour.short} Русскоговорящий гид Али сопровождает вас лично — от встречи до
                    возврата. Никаких групп из автобуса, только ваш темп, ваши интересы и живой рассказ
                    о культуре, истории и повседневной жизни Индии.
                  </>
                )}
              </p>
              {tour.longDescription && tour.longDescription.length > 0 && (
                <div className="mt-6 space-y-4 text-foreground/80 leading-relaxed text-[14.5px]" data-testid="long-description">
                  {tour.longDescription.map((p, i) => (
                    p.startsWith("### ")
                      ? <h4 key={i} className="font-display text-xl md:text-2xl text-foreground pt-3">{p.slice(4)}</h4>
                      : <p key={i}>{p}</p>
                  ))}
                </div>
              )}
              <p className="mt-4 text-foreground/70 leading-relaxed text-[14px]">
                Экскурсия «{tour.title}» проходит в {tour.city}, длится {tour.duration.toLowerCase()}, рассчитана на группу {tour.group.toLowerCase()}. Формат — {isGroup ? "групповой, с фиксированными датами заезда" : "приватный, только для вас и вашей компании"}.
                {tour.priceNote ? ` Стоимость: ${tour.priceNote}` : ` Стоимость: ${tour.price}`}
              </p>
            </div>

            <div>
              <p className="overline text-[#D0533C]">Программа</p>
              <h3 className="font-display text-2xl md:text-3xl mt-4">Ключевые моменты маршрута</h3>
              <ol className="mt-6 space-y-4" data-testid="itinerary-list">
                {(tour.program || tour.highlights.map((h) => ({ title: h, desc: "" }))).map((item, i) => {
                  const it = typeof item === "string" ? { title: item, desc: "" } : item;
                  return (
                    <li key={i} data-testid={`itinerary-item-${i}`} className="flex gap-5 pb-5 border-b border-border last:border-none">
                      <span className="font-display text-3xl text-[#F07E26] leading-none pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <div className="pt-0.5">
                        <p className="font-semibold text-foreground leading-snug">{it.title}</p>
                        {it.desc && <p className="mt-2 text-foreground/70 leading-relaxed text-[14px] whitespace-pre-line">{it.desc}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            <div>
              <p className="overline text-[#D0533C]">Что включено</p>
              <h3 className="font-display text-2xl md:text-3xl mt-4">В стоимость входит</h3>
              <ul className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="includes-list">
                {tour.includes.map((inc, i) => (
                  <li key={i} data-testid={`include-item-${i}`} className="flex items-start gap-3 text-foreground/80">
                    <Check className="h-4 w-4 text-[#F07E26] mt-1 shrink-0" />
                    <span>{inc}</span>
                  </li>
                ))}
              </ul>
              {tour.notIncluded && tour.notIncluded.length > 0 && (
                <div className="mt-8" data-testid="not-included-block">
                  <h4 className="font-display text-xl md:text-2xl">Не включено</h4>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                    {tour.notIncluded.map((it, i) => (
                      <li key={i} data-testid={`not-included-${i}`} className="flex items-start gap-3 text-foreground/70">
                        <span className="text-[#D0533C] mt-1 leading-none text-lg">×</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {tour.organizational && tour.organizational.length > 0 && (
              <div data-testid="organizational-block">
                <p className="overline text-[#D0533C]">Организационные детали</p>
                <h3 className="font-display text-2xl md:text-3xl mt-4">Как проходит день</h3>
                <ul className="mt-6 space-y-3">
                  {tour.organizational.map((it, i) => (
                    <li key={i} data-testid={`org-item-${i}`} className="flex items-start gap-3 text-foreground/80 leading-relaxed">
                      <span className="text-[#F07E26] mt-1.5 leading-none">◆</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border-t border-border pt-10">
              <p className="overline text-[#D0533C]">Частые вопросы</p>
              <h3 className="font-display text-2xl md:text-3xl mt-4">Полезно знать</h3>
              <div className="mt-6">
                {FAQ.slice(0, 4).map((q, i) => (
                  <details key={i} data-testid={`tour-faq-${i}`} className="group border-b border-border py-4">
                    <summary className="cursor-pointer flex items-center justify-between font-display text-lg md:text-xl">
                      {q.q}
                      <span className="text-[#F07E26] text-2xl group-open:rotate-45 transition-transform">+</span>
                    </summary>
                    <p className="mt-3 text-foreground/70 leading-relaxed text-[14px]">{q.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          {/* Sticky sidebar */}
          <aside className="md:col-span-5">
            <div className="md:sticky md:top-28 border border-border bg-white rounded-lg p-6 md:p-8">
              {isGroup && (
                <div className="mb-5 -mx-6 -mt-6 md:-mx-8 md:-mt-8 px-6 py-4 md:px-8 bg-[#1A2035] text-[#FAF7F2] rounded-t-lg">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <span
                      className="bg-[#F5C58C] text-[#1A2035] px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold"
                      data-testid="tour-format-badge"
                    >
                      Формат групповой
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold ${
                        tour.frequency === "weekly"
                          ? "bg-[#2E7D5B] text-white"
                          : "bg-[#D0533C] text-white"
                      }`}
                      data-testid="tour-frequency-badge"
                    >
                      {tour.frequency === "weekly" ? "Еженедельно" : "Едем вместе — на праздники"}
                    </span>
                  </div>
                  <p className="font-display text-xl mt-1" data-testid="tour-dates">{tour.dates}</p>
                </div>
              )}              <p className="overline text-muted-foreground">Стоимость</p>
              <p className="font-display text-4xl mt-2" data-testid="tour-price">{tour.price}</p>
              {tour.priceNote && (
                <p className="mt-2 text-[13px] text-muted-foreground leading-snug">{tour.priceNote}</p>
              )}
              <div className="mt-6 space-y-3 text-sm text-foreground/80">
                <p className="flex items-center gap-3"><Clock className="h-4 w-4 text-[#F07E26]" /> {tour.duration}</p>
                <p className="flex items-center gap-3"><Users className="h-4 w-4 text-[#F07E26]" /> {tour.group}</p>
                <p className="flex items-center gap-3"><MapPin className="h-4 w-4 text-[#F07E26]" /> {tour.city}</p>
                <p className="flex items-center gap-3"><Check className="h-4 w-4 text-[#F07E26]" /> Русскоговорящий гид Али</p>
                <p className="flex items-center gap-3"><Check className="h-4 w-4 text-[#F07E26]" /> Отмена без штрафа за 24ч</p>
              </div>
              <button
                data-testid="sidebar-book-btn"
                onClick={() => setBookingOpen(true)}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F07E26] text-white px-6 py-3.5 text-[14px] font-semibold hover:bg-[#d86a15]"
              >
                Забронировать <ArrowUpRight className="h-4 w-4" />
              </button>
              {tour.externalUrl && (
                <a
                  href={tour.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sidebar-external"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#1A2035] text-[#1A2035] px-6 py-3.5 text-[14px] font-medium hover:bg-[#1A2035] hover:text-white transition"
                >
                  Забронировать на YouTravel <ArrowUpRight className="h-4 w-4" />
                </a>
              )}
              <a
                href={BRAND.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sidebar-whatsapp"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-foreground/20 text-foreground px-6 py-3.5 text-[14px] font-medium hover:bg-[#FAF7F2]"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <a
                href={BRAND.telegramHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sidebar-telegram"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-foreground/20 text-foreground px-6 py-3.5 text-[14px] font-medium hover:bg-[#FAF7F2]"
              >
                <Send className="h-4 w-4" /> Telegram
              </a>
              <p className="mt-5 text-[12px] text-muted-foreground text-center">
                Ответим в мессенджерах в течение 1–2 часов.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* Related tours */}
      <section className="py-16 md:py-24 bg-paper border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <p className="overline text-[#D0533C]">Похожие экскурсии</p>
          <h2 className="font-display text-3xl md:text-4xl mt-4">Что ещё выбирают путешественники</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((t) => (
              <Link
                key={t.id}
                to={`/tours/${t.id}`}
                data-testid={`related-tour-${t.id}`}
                className="group block"
              >
                <div className="relative overflow-hidden rounded-md aspect-[4/5] bg-muted">
                  <img src={t.image} alt={t.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
                  <div className="absolute top-4 left-4 bg-[#FAF7F2]/95 backdrop-blur px-3 py-1.5 rounded-full">
                    <span className="overline text-foreground/80">{t.city}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-5 text-[12px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t.duration}</span>
                  <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {t.group}</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl mt-2 leading-tight">{t.title}</h3>
                <p className="font-display text-lg mt-2 text-[#D0533C]">{t.price}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Compact footer */}
      <footer className="bg-[#1A2035] text-[#FAF7F2] py-14">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid md:grid-cols-3 gap-8">
          <div>
            <p className="font-display text-2xl">
              <span className="font-semibold">ДЕЛИ</span>
              <span className="opacity-60 mx-2">·</span>
              <span className="italic">с Али</span>
            </p>
            <p className="mt-3 text-[#FAF7F2]/70 text-sm max-w-xs">
              Частные экскурсии с русскоговорящим гидом Али по всей Индии.
            </p>
          </div>
          <div>
            <p className="overline text-[#F5C58C]">Контакты</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a href={BRAND.phoneHref} className="inline-flex items-center gap-2 hover:text-[#F07E26]"><Phone className="h-4 w-4" /> {BRAND.phone}</a></li>
              <li><a href={BRAND.emailHref} className="inline-flex items-center gap-2 hover:text-[#F07E26]"><Mail className="h-4 w-4" /> {BRAND.email}</a></li>
            </ul>
          </div>
          <div>
            <p className="overline text-[#F5C58C]">Мессенджеры</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={BRAND.whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#2A3352] px-3 py-1.5 text-sm hover:bg-[#F07E26]"><MessageCircle className="h-4 w-4" />WhatsApp</a>
              <a href={BRAND.telegramHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#2A3352] px-3 py-1.5 text-sm hover:bg-[#F07E26]"><Send className="h-4 w-4" />Telegram</a>
            </div>
          </div>
        </div>
      </footer>

      <WhatsAppFloat />
      <BookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        tour={tour}
        tours={[...TOURS, ...GROUP_TOURS]}
        onSubmit={handleSubmit}
        onSuccess={() => toast.success("Заявка отправлена — Али свяжется с вами в течение 1–2 часов")}
        onError={(msg) => toast.error(msg || "Не удалось отправить заявку. Попробуйте ещё раз.")}
      />
    </div>
  );
}
